import type { DoneInvokeEvent, EventObject, PropertyMapper } from 'xstate';
import { createMachine } from 'xstate';
import { getServiceProducts } from '@hotjar/service-management';
import { assign, sendParent } from 'xstate/lib/actions';
import * as Sentry from '@sentry/browser';
import type { ProductAllowanceFetcherContext } from './product-allowance-fetcher.machine.types';
import { getUpdatedProductAllowances } from '../utils/events';
import { mapServiceProductToProductAllowance } from '~/subscription-management/utils/mappers';
import {
  convertAllowancesToBasic,
  planIsNotBasic,
} from '~/subscription-management/utils/allowance.utils';
import type { ServiceProductsResponse } from '~/service-management/api/service-products/model';

export const productsAllowanceFetcherMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./product-allowance-fetcher.machine.typegen').Typegen0,
    id: 'products-fetcher',
    initial: 'getAllowances',
    schema: {
      context: {} as ProductAllowanceFetcherContext,
      services: {} as {
        getServiceProducts: { data: ServiceProductsResponse };
      },
    },
    states: {
      getAllowances: {
        invoke: {
          src: 'getServiceProducts',
          onDone: [
            {
              target: 'updateAllowancesToAdd',
              actions: assign<
                ProductAllowanceFetcherContext,
                DoneInvokeEvent<ServiceProductsResponse>
              >({
                allEffectiveAllowances: (_context, event) =>
                  event.data.effectiveNow.map(mapServiceProductToProductAllowance),
                allRenewedAllowances: (_context, event) =>
                  event.data.renewWith.map(mapServiceProductToProductAllowance),
                isOnTrial: false,
              }),
            },
          ],
          onError: [
            {
              actions: [
                sendParent<
                  ProductAllowanceFetcherContext,
                  DoneInvokeEvent<ServiceProductsResponse>
                >('NOTIFICATION'),
                (_, event) => {
                  Sentry.captureMessage(
                    `Error fetching products for Billing 3.0: ${JSON.stringify(event.data)}`,
                  );
                  Sentry.captureException(event.data);
                },
              ],
            },
          ],
        },
      },
      updateAllowancesToAdd: {
        always: [
          {
            target: 'done',
            cond: 'shouldUpdateAllowances',
            actions: assign<ProductAllowanceFetcherContext>((context) => ({
              /**
               * at this point we have a first time conversion on trial.
               * the "initial"/allEffectiveAllowances should be basic to accurately reflect the intention of the UI
               * in the case of trial we change allEffectiveAllowances to basic and add the higher plans to allowancesToAdd
               */
              allEffectiveAllowances: convertAllowancesToBasic(context.allEffectiveAllowances),
              allRenewedAllowances: convertAllowancesToBasic(context.allRenewedAllowances),
              ...getUpdatedProductAllowances({
                allAllowances: [],
                previouslyAddedAllowances: [],
                toAddAllowances: context.allEffectiveAllowances,
                hasActiveSubscription: context.hasActiveSubscription,
              }),
              isOnTrial: true,
            })),
          },
          { target: 'done' },
        ],
      },
      done: {
        type: 'final',
        data: {
          allEffectiveAllowances: (context) => context.allEffectiveAllowances,
          allRenewedAllowances: (context) => context.allRenewedAllowances,
          allowancesToAdd: (context) => context.allowancesToAdd,
          isOnTrial: (context) => context.isOnTrial,
        } as PropertyMapper<
          ProductAllowanceFetcherContext,
          EventObject,
          ProductAllowanceFetcherContext
        >,
      },
    },
  },
  {
    services: {
      getServiceProducts: (context) => getServiceProducts(context.accountId),
    },
    guards: {
      shouldUpdateAllowances: (context) =>
        context.hasActiveSubscription === false &&
        context.allEffectiveAllowances.some((allowance) => planIsNotBasic(allowance)),
    },
  },
);
